import {
  defaultDataIdFromObject,
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import { get, has } from 'lodash';
import uuid from 'uuid/v4';

import introspectionQueryResultData from './fragmentTypes.json';

/**
 * Use the cached fragment data from ./fragmentTypes.json to improve
 * fragment cache matching.
 * @type {IntrospectionFragmentMatcher}
 */
const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
});

/**
 * Given an object and a path within that object, return the value
 * at that path OR return a random UUID (and issue an error to the
 * console.
 *
 * @param {object} object
 * @param {string} path
 * @return {string}
 */
function getKeyComponent(object, path) {
  if (has(object, path)) {
    return get(object, path);
  }

  // NOTE: This is very useful for debugging, but becomes very noisey with certain types.
  // eslint-disable-next-line no-console
  console.error(
    `[Apollo Client InMemoryCache] object does not contain an embedded '${path}' necessary for generating a hash key. Falling back to a random key. Object:`,
    object,
  );

  return uuid();
}

/**
 * Map of GraphQL object `__typename` values to function that will be used
 * to generate a unique cache key for that type of object.
 * @type {{{string}: (function(object): string)}}
 */
const objectToIdMappers = {
  AfterHoursEventInstance: object => {
    const eventId = getKeyComponent(object, 'afterHoursEvent.id');
    const start = getKeyComponent(object, 'start');
    return `AfterHoursEventInstance:${eventId}:${start}`;
  },
  // The CustomCustomer/CustomStoreCustomer type comes through as a nested object, which does not
  // trigger a rerender of react components when it is updated. Adding an ID
  // allows apollo to update _all_ queries attached to this type.
  CustomCustomer: object => {
    const name = get(object, 'name');
    return name ? `CustomCustomer:${name}` : null;
  },

  CustomStoreCustomer: object => {
    const name = get(object, 'name');
    return name ? `CustomStoreCustomer:${name}` : null;
  },

  TireOption: object =>
    `TireOption:${get(object, 'id')}-${get(object, 'name')}`,

  UserInfo: object => `UserInfo:${get(object, 'email')}`,
  UserRole: object => `UserRole:${get(object, 'name')}`,

  CaseAgreementLine: object =>
    `CaseAgreementLine:${get(object, 'agreementLine.id')}`,

  CaseSuppliedLine: object =>
    `CaseSuppliedLine:${get(object, 'suppliedLine.id')}`,
};

/**
 * Generate custom cache keys for certain object types.
 *
 * Exported for testing.
 *
 * @param object
 * @return {string}
 */
export function dataIdFromObject(object) {
  const typename = get(object, '__typename');
  const customKeyFn = objectToIdMappers[typename];

  return (customKeyFn || defaultDataIdFromObject)(object);
}

/**
 * Build the in-memory cache for use by the Apollo Client.
 * @return {InMemoryCache}
 */
export default function buildCache() {
  return new InMemoryCache({
    fragmentMatcher,
    dataIdFromObject,
  });
}



// WEBPACK FOOTER //
// ./src/setup/ApolloConnector/cache/index.js